import React, { useState, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const ETF_COLORS = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', 
  '#06b6d4', '#ec4899', '#f97316', '#84cc16', '#a855f7'
];

const ETFComparisonChart = ({ data, spyYearly }) => {
  const [period, setPeriod] = useState("3m"); // 1m, 3m, 6m, 1y
  const [showSpy, setShowSpy] = useState(true);
  const [filterMode, setFilterMode] = useState("top5");

  const periodDays = useMemo(() => {
    if (period === "1m") return 21;
    if (period === "3m") return 63;
    if (period === "6m") return 126;
    return 252;
  }, [period]);

  const returnField = useMemo(() => {
    if (period === "1m") return "return_1m";
    if (period === "3m") return "return_3m";
    if (period === "6m") return "return_6m";
    return "return_1y";
  }, [period]);

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return { datasets: [] };

    const sorted = [...data].sort((a, b) => (b[returnField] || -999) - (a[returnField] || -999));
    const n = filterMode === "top10" ? 10 : 5;
    const topEtfs = sorted.slice(0, n);

    const datasets = topEtfs.map((etf, idx) => {
      const yc = etf.yearly_closes || [];
      const slice = yc.slice(-periodDays);
      if (slice.length < 2) return null;
      
      const base = slice[0].close || 1;
      return {
        label: etf.ticker,
        data: slice.map(p => ({ x: p.time, y: ((p.close / base) - 1) * 100 })),
        borderColor: ETF_COLORS[idx % ETF_COLORS.length],
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.1,
      };
    }).filter(d => d !== null);

    if (showSpy && spyYearly.length >= 2) {
      const spySlice = spyYearly.slice(-periodDays);
      const base = spySlice[0].close || 1;
      datasets.push({
        label: "SPY (Benchmark)",
        data: spySlice.map(p => ({ x: p.time, y: ((p.close / base) - 1) * 100 })),
        borderColor: "#ffffff",
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.1,
      });
    }

    // Extract labels from the first dataset
    const labels = datasets.length > 0 ? datasets[0].data.map(d => d.x) : [];

    return {
      labels,
      datasets
    };
  }, [data, spyYearly, periodDays, returnField, filterMode, showSpy]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#71717a',
          font: { size: 10, weight: 'bold', family: 'Inter' },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: '#09090b',
        borderColor: '#27272a',
        borderWidth: 1,
        titleColor: '#e4e4e7',
        bodyColor: '#a1a1aa',
        padding: 10,
        bodyFont: { size: 11 },
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y >= 0 ? '+' : ''}${ctx.parsed.y.toFixed(2)}%`
        }
      }
    },
    scales: {
      x: {
        grid: { color: 'rgba(255, 255, 255, 0.05)' },
        ticks: { 
          color: '#52525b', 
          font: { size: 9 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12
        }
      },
      y: {
        grid: { color: 'rgba(255, 255, 255, 0.05)' },
        ticks: { 
          color: '#52525b', 
          font: { size: 9 },
          callback: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`
        }
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-900 pb-4">
        <div className="flex gap-1.5 p-1 bg-zinc-900 rounded-lg border border-zinc-800">
          {['1m', '3m', '6m', '1y'].map(p => (
            <Button 
              key={p} 
              variant={period === p ? "secondary" : "ghost"} 
              size="sm" 
              className="h-7 px-3 text-[12px] uppercase font-bold"
              onClick={() => setPeriod(p)}
            >
              {p}
            </Button>
          ))}
        </div>

        <div className="flex items-center gap-6">
           <div className="flex items-center space-x-2 bg-zinc-900 px-3 py-1.5 rounded-lg border border-zinc-800">
             <Checkbox 
               id="spy-toggle" 
               checked={showSpy} 
               onCheckedChange={setShowSpy} 
             />
             <Label htmlFor="spy-toggle" className="text-[12px] font-bold text-zinc-400 uppercase tracking-widest cursor-pointer">
               Show SPY
             </Label>
           </div>

           <div className="flex gap-1.5 p-1 bg-zinc-900 rounded-lg border border-zinc-800">
             <Button 
               variant={filterMode === "top5" ? "secondary" : "ghost"} 
               size="sm" 
               className="h-7 px-3 text-[12px] uppercase font-bold"
               onClick={() => setFilterMode("top5")}
             >
               Top 5
             </Button>
             <Button 
               variant={filterMode === "top10" ? "secondary" : "ghost"} 
               size="sm" 
               className="h-7 px-3 text-[12px] uppercase font-bold"
               onClick={() => setFilterMode("top10")}
             >
               Top 10
             </Button>
           </div>
        </div>
      </div>

      <div className="h-[400px] w-full bg-black/20 rounded-xl p-4">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default ETFComparisonChart;
